import { useContext, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import type { IFetchOauthClientsParams, IOauthClient } from "@mne-authentication/api";
import { SessionContext } from "@mne-authentication/session2";

import { useSnackbar } from "@mnemonic/feature-snackbar";
import { defineMessages, useIntl } from "@mnemonic/intl";
import type { ITableSimpleRow, TTablePaginationProps, TTableSimpleHeaders } from "@mnemonic/mui";
import { extractErrorMessage } from "@mnemonic/redux-utils";

import { useFetchOauthClientsQuery } from "../../Redux/Service/openIdApi";

const DEFAULT_PAGE_SIZE = 25;

const DEFAULT_CRITERIA: IFetchOauthClientsParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  excludeFlags: ["hidden"],
  sortBy: ["name"],
};

const messages = defineMessages({
  fetchOauthClientsError: {
    id: "client.overview.fetchOauthClients.error",
    defaultMessage: "Error fetching oauth clients",
  },
});

interface ICustomerCheckboxState {
  showCustomerApps: boolean;
  showNonCustomerApps: boolean;
}

export type TClientColumnId = "name" | "url" | "description" | "customer";
type TSortBy = "name" | "-name";

interface IUseClientOverviewPage {
  columns: TTableSimpleHeaders<TClientColumnId>[];
  isFetching: boolean;
  keywordValue: string;
  paginationProps: TTablePaginationProps;
  rows: ITableSimpleRow<TClientColumnId>[];
  setCustomerCheckboxState: Dispatch<SetStateAction<ICustomerCheckboxState>>;
  setKeywordValue: Dispatch<SetStateAction<string>>;
  updateCriteria(newCriteria: Partial<IFetchOauthClientsParams>): void;
}

const filterClients = (state: ICustomerCheckboxState, clients: IOauthClient[]) => {
  if (state.showCustomerApps === state.showNonCustomerApps) {
    return clients;
  } else if (state.showCustomerApps) {
    return clients.filter((client) => client.customer);
  } else if (state.showNonCustomerApps) {
    return clients.filter((client) => !client.customer);
  }

  return clients;
};

const getCustomerSecondaryText = (client: IOauthClient, domainName?: string) =>
  [
    domainName === client?.customer?.domain?.name ? undefined : client?.customer?.domain?.name,
    client?.customer?.shortName,
  ]
    .filter(Boolean)
    .join("/");

export const useClientOverviewPage = (): IUseClientOverviewPage => {
  const { session } = useContext(SessionContext);
  const [criteria, setCriteria] = useState<IFetchOauthClientsParams>(DEFAULT_CRITERIA);
  const [sortBy, setSortBy] = useState<TSortBy>("name");
  const [keywordValue, setKeywordValue] = useState("");
  const [customerCheckboxState, setCustomerCheckboxState] = useState<ICustomerCheckboxState>({
    showCustomerApps: false,
    showNonCustomerApps: false,
  });
  const oAuthClientsResults = useFetchOauthClientsQuery({ criteria });
  const { error } = useSnackbar();
  const { formatMessage } = useIntl();
  const fetchOauthClientsErrorMessage = formatMessage(messages.fetchOauthClientsError);

  const updateCriteria = (newCriteria: Partial<IFetchOauthClientsParams>) => {
    setCriteria((currentCriteria) => ({ ...currentCriteria, ...newCriteria }));
  };

  const handleSortByChange = () => {
    const newSortBy = sortBy === "name" ? "-name" : "name";

    setSortBy(newSortBy);
    updateCriteria({ offset: 0, sortBy: [newSortBy] });
  };

  const handlePageChange: TTablePaginationProps["onPageChange"] = (_event, page) => {
    updateCriteria({ offset: page * (criteria.limit || DEFAULT_PAGE_SIZE) });
  };

  const handleRowsPerPageChange: TTablePaginationProps["onRowsPerPageChange"] = (event) => {
    updateCriteria({ limit: parseInt(event.target.value, 10), offset: 0 });
  };

  const columns: TTableSimpleHeaders<TClientColumnId>[] = [
    {
      columnId: "name",
      kind: "text-header",
      text: formatMessage({ id: "client.overview.name", defaultMessage: "Name" }),
      sort: {
        isSorted: sortBy === "name" || sortBy === "-name",
        direction: sortBy.startsWith("-") ? "desc" : "asc",
        onSortByChangeFn: handleSortByChange,
      },
    },
    { columnId: "url", kind: "text-header", text: formatMessage({ id: "client.overview.url", defaultMessage: "URL" }) },
    {
      columnId: "description",
      kind: "text-header",
      text: formatMessage({ id: "client.overview.description", defaultMessage: "Description" }),
    },
    { columnId: "customer", kind: "text-header", text: formatMessage({ id: "Customer", defaultMessage: "Customer" }) },
  ];

  const clients = filterClients(customerCheckboxState, oAuthClientsResults?.data?.data || []);
  const rows: ITableSimpleRow<TClientColumnId>[] = clients.map((client) => ({
    id: client.name,
    cells: [
      { columnId: "name", kind: "text", text: client.name },
      {
        columnId: "url",
        kind: "link",
        text: client.mainURI,
        linkProps: { component: "a", href: client.mainURI, target: "_blank" },
      },
      { columnId: "description", kind: "text", text: client.description },
      client.customer
        ? {
            columnId: "customer",
            kind: "text",
            text: client.customer.name,
            secondaryText: getCustomerSecondaryText(client, session?.user?.domain?.name),
          }
        : {
            columnId: "customer",
            kind: "text",
            text: formatMessage({
              id: "client.overview.non.customer.specific",
              defaultMessage: "Non customer specific",
            }),
            textTypographyProps: { color: "textSecondary" },
          },
    ],
  }));
  const rowsPerPage = criteria.limit || DEFAULT_PAGE_SIZE;
  const paginationProps: TTablePaginationProps = {
    count: oAuthClientsResults?.data?.count || 0,
    page: Math.floor((criteria.offset || 0) / rowsPerPage),
    rowsPerPage,
    onPageChange: handlePageChange,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: oAuthClientsResults?.isFetching,
    onRowsPerPageChange: handleRowsPerPageChange,
  };

  useEffect(() => {
    if (oAuthClientsResults?.isError) {
      error({
        size: "large",
        title: fetchOauthClientsErrorMessage,
        message: extractErrorMessage(oAuthClientsResults.error) || fetchOauthClientsErrorMessage,
      });
    }
  }, [error, fetchOauthClientsErrorMessage, oAuthClientsResults.error, oAuthClientsResults?.isError]);

  return {
    columns,
    isFetching: Boolean(oAuthClientsResults?.isFetching),
    keywordValue,
    paginationProps,
    rows,
    setCustomerCheckboxState,
    setKeywordValue,
    updateCriteria,
  };
};
